import cn from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { ButtonUI, Card, IconTooltip, ModalUI, Tag } from 'sonata-react-components';

import { ImportMode, usePOIImporter } from '@/components/common/POIImporter/POIImporter.context';

import './attributionPOISelection.scss';

type State = {
  selectedImportMode: ImportMode | null;
};

type Props = {
  attributionSubsection?: 'attributionPOIs' | 'proximityPoints';
};

export function AttributionPOISelection({ attributionSubsection }: Props) {
  const { formatMessage } = useIntl();
  const modalRef = useRef<React.ElementRef<typeof ModalUI>>(null);
  const { setImportMode, setShowModal } = usePOIImporter();
  const [{ selectedImportMode }, setState] = useState<State>({
    selectedImportMode: null
  });

  const baseId = 'attributionPOISelection';

  useEffect(() => {
    modalRef.current?.openModal?.();
  }, [modalRef.current]);

  const handleSelectionChange = (mode: ImportMode) => {
    setState({ selectedImportMode: mode });
  };

  const handleNext = () => {
    setImportMode(selectedImportMode);
  };

  const isInventoryMode = selectedImportMode === 'inventory';
  const isUploadMode = selectedImportMode === 'upload';

  return (
    <ModalUI
      closeModalCallback={() => setShowModal(false)}
      description={formatMessage(
        attributionSubsection === 'attributionPOIs'
          ? {
              defaultMessage: "Tell us how you'd like to add attribution so we can guide you through the next steps.",
              id: 'attributionPOI.selectType.description'
            }
          : {
              defaultMessage:
                "Tell us how you'd like to add proximity points so we can guide you through the next steps.",
              id: 'proximityPoints.selectType.description'
            }
      )}
      leftButton={
        <ButtonUI
          onClick={() => setShowModal(false)}
          shape="squareRounded"
          text={formatMessage({
            defaultMessage: 'Cancel',
            id: 'common.cancel'
          })}
          type="secondary"
        />
      }
      ref={modalRef}
      rightButton={
        <ButtonUI
          disabled={!selectedImportMode}
          icon="fal fa-arrow-right"
          iconPosition="right"
          onClick={handleNext}
          shape="squareRounded"
          text={formatMessage({
            defaultMessage: 'Next',
            id: 'common.next'
          })}
          type="primary"
        />
      }
      title={formatMessage(
        attributionSubsection === 'attributionPOIs'
          ? {
              defaultMessage: 'Add Attribution POI',
              id: 'attributionPOI.selectType.title'
            }
          : {
              defaultMessage: 'Add Proximity Points',
              id: 'proximityPoints.selectType.title'
            }
      )}
      width="647px"
    >
      <div className="attributionPOISelection" data-testid="attribution-poi-selection-modal-container">
        <Card
          className="attributionPOISelection__card"
          isSelected={isInventoryMode}
          onClick={() => handleSelectionChange('inventory')}
        >
          <Card.Header className="attributionPOISelection__cardHeader">
            <Tag
              className={cn('attributionPOISelection__cardHeaderTag', {
                'attributionPOISelection__cardHeaderTag--selected': isInventoryMode
              })}
              id={`${baseId}-inventory-tag`}
              status="custom"
            >
              {formatMessage(
                attributionSubsection === 'attributionPOIs'
                  ? {
                      defaultMessage: 'Multiple POIs',
                      id: 'attributionPOI.selectType.tag'
                    }
                  : {
                      defaultMessage: 'Multiple Points',
                      id: 'proximityPoints.selectType.tag'
                    }
              )}
            </Tag>
            <IconTooltip
              customClassName={cn('attributionPOISelection__cardHeaderInfo', {
                'attributionPOISelection__cardHeaderInfo--selected': isInventoryMode
              })}
              event="mouseenter"
              icon="fa-regular fa-info"
              place="top"
            >
              <p>
                {formatMessage({
                  defaultMessage:
                    'Get access to a curated collection of commercial areas sourced from diverse public registries and databases.',
                  id: 'attributionPOI.selectType.inventory.info'
                })}
              </p>
            </IconTooltip>
          </Card.Header>
          <Card.Media
            className={cn('attributionPOISelection__cardMedia', {
              'attributionPOISelection__cardMedia--selected': isInventoryMode
            })}
            {...(attributionSubsection === 'proximityPoints' && { icon: 'fa-duotone fa-thumbtack' })}
          />
          <Card.Label className="attributionPOISelection__cardTitle" variant="title">
            {formatMessage(
              attributionSubsection === 'attributionPOIs'
                ? {
                    defaultMessage: 'POI Inventory',
                    id: 'attributionPOI.selectType.inventory.title'
                  }
                : {
                    defaultMessage: 'Points Inventory',
                    id: 'proximityPoints.selectType.inventory.title'
                  }
            )}
          </Card.Label>
          <Card.Label className="attributionPOISelection__cardDescription" ellipsis={false} variant="subtitle">
            {formatMessage(
              attributionSubsection === 'attributionPOIs'
                ? {
                    defaultMessage:
                      'You can integrate pre-existing Points of Interest compiled from various public and commercial providers, and measure their  attribution.',
                    id: 'attributionPOI.selectType.inventory.description'
                  }
                : {
                    defaultMessage:
                      'Reuse a custom list of coordinates from “My Venues” inventory to quickly set up your attribution areas.',
                    id: 'proximityPoints.selectType.inventory.description'
                  }
            )}
          </Card.Label>
        </Card>
        <Card
          className="attributionPOISelection__card"
          isSelected={isUploadMode}
          onClick={() => handleSelectionChange('upload')}
        >
          <Card.Header className="attributionPOISelection__cardHeader">
            <Tag
              className={cn('attributionPOISelection__cardHeaderTag', {
                'attributionPOISelection__cardHeaderTag--selected': isUploadMode
              })}
              id={`${baseId}-upload-tag`}
              status="custom"
            >
              {formatMessage(
                attributionSubsection === 'attributionPOIs'
                  ? {
                      defaultMessage: 'Multiple POIs',
                      id: 'attributionPOI.selectType.tag'
                    }
                  : {
                      defaultMessage: 'Multiple Points',
                      id: 'proximityPoints.selectType.tag'
                    }
              )}
            </Tag>
            <IconTooltip
              customClassName={cn('attributionPOISelection__cardHeaderInfo', {
                'attributionPOISelection__cardHeaderInfo--selected': isUploadMode
              })}
              event="mouseenter"
              icon="fa-regular fa-info"
              place="top"
            >
              <p>
                {formatMessage({
                  defaultMessage:
                    'Points of Interest (POIs) are commercial areas with high public visibility. Use this to upload and customize businesses that demonstrate significant footfall and commercial engagement.',
                  id: 'attributionPOI.selectType.upload.info'
                })}
              </p>
            </IconTooltip>
          </Card.Header>
          <Card.Media
            className={cn('attributionPOISelection__cardMedia', {
              'attributionPOISelection__cardMedia--selected': isUploadMode
            })}
            icon={
              attributionSubsection === 'attributionPOIs'
                ? 'fa-kit fa-sharp-regular-location-dot-circle-plus'
                : 'fa-duotone fa-file-csv'
            }
          />
          <Card.Label className="attributionPOISelection__cardTitle" variant="title">
            {formatMessage(
              attributionSubsection === 'attributionPOIs'
                ? {
                    defaultMessage: 'Upload your POIs',
                    id: 'attributionPOI.selectType.upload.title'
                  }
                : {
                    defaultMessage: 'Upload your Points',
                    id: 'proximityPoints.selectType.upload.title'
                  }
            )}
          </Card.Label>
          <Card.Label className="attributionPOISelection__cardDescription" ellipsis={false} variant="subtitle">
            {formatMessage(
              attributionSubsection === 'attributionPOIs'
                ? {
                    defaultMessage:
                      'Upload your own POIs. Note that our system only supports businesses with high public footfall or significant engagement as POIs.',
                    id: 'attributionPOI.selectType.upload.description'
                  }
                : {
                    defaultMessage:
                      'Upload your own list of locations using coordinates. These points will automatically generate a 50m radius for attribution.',
                    id: 'proximityPoints.selectType.upload.description'
                  }
            )}
          </Card.Label>
        </Card>
      </div>
    </ModalUI>
  );
}
